<template>
  <div>
    <!-- Cabeçalho -->
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h5 class="mb-0">Planos de Contas</h5>
      <BButton
        v-if="hasPermission('adicionar_planos_contas')"
        variant="primary"
        class="d-flex align-items-center"
        @click="irParaCadastro"
      >
        <i class="bi bi-plus-circle-fill mr-2" title="Novo Plano"></i>      </BButton>
    </div>

    <!-- Filtros -->
    <div class="d-flex mb-3" style="gap: 10px;">
      <input
        v-model="filters.nome"
        placeholder="Filtrar por Nome"
        class="form-control same-height"
        @input="filtrar"
      />
      <b-button
        variant="secondary"
        @click="limparFiltros"
        class="d-flex align-items-center justify-content-center"
      >
        <i class="bi bi-funnel mr-2" title="Limpar Filtro"></i>
      </b-button>    </div>

    <!-- Tabela nativa com ordenação -->
    <table class="table table-striped table-hover">
      <thead>
        <tr>
          <th>
            <button class="btn btn-link p-0" @click="sort('nome')">
              Nome {{ arrow('nome') }}
            </button>
          </th>
          <th>
            <button class="btn btn-link p-0" @click="sort('tipo')">
              Tipo {{ arrow('tipo') }}
            </button>
          </th>
          <th>Descrição</th>
          <th>
            <button class="btn btn-link p-0" @click="sort('ativo')">
              Status {{ arrow('ativo') }}
            </button>
          </th>
          <th class="text-end">Ações</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="row in items" :key="row.id">
          <td>{{ row.nome }}</td>
          <td>
            <span :class="row.tipo === 'Receita' ? 'text-primary' : 'text-danger'">
              {{ row.tipo }}
            </span>
          </td>
          <td>{{ row.descricao }}</td>
          <td>{{ row.ativo ? 'Ativo' : 'Inativo' }}</td>
          <td class="text-end">
            <router-link
              v-if="hasPermission('detalhar_planos_contas')"
              :to="`/financeiro/planos-contas/${row.id}/detalhar`"
            >
              <i class="bi bi-eye-fill text-primary me-2" title="Detalhar"></i>
            </router-link>
            <router-link
              v-if="hasPermission('alterar_planos_contas')"
              :to="`/financeiro/planos-contas/${row.id}`"
            >
              <i class="bi bi-pencil-fill text-warning me-2" title="Editar"></i>
            </router-link>
            <i
              v-if="hasPermission('excluir_planos_contas')"
              class="bi bi-trash-fill text-danger me-2"
              @click="excluir(row.id)"
              title="Excluir"
            ></i>
          </td>
        </tr>
      </tbody>
    </table>

    <!-- Paginação -->
    <div class="d-flex justify-content-between align-items-center mt-3">
      <!-- resumo -->
      <div>
        Mostrando {{ startRow }} a {{ endRow }} de {{ totalItems }} registros
      </div>

      <!-- seletor de itens por página -->
      <div class="d-flex align-items-center" style="gap: .5rem;">
        <b-form-select
          v-model="limit"
          :options="pageOptions.map(n => ({ value: n, text: n + ' / página' }))"
          size="sm"
          style="width: 6rem"
        />
      </div>

      <!-- controle de página -->
      <div class="d-flex align-items-center">
        <b-pagination
          v-model="page"
          :per-page="limit"
          :total-rows="totalItems"
          size="sm"
          limit="5"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { onMounted, computed, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useSortableTable } from '@/utils/useSortableTable';
import { usePermissions } from '@/composables/usePermissions';
import { BButton, BFormSelect, BPagination } from 'bootstrap-vue-next';
import axios from '@/services/axios';

const router = useRouter();
const { fetchPermissions, hasPermission } = usePermissions();

// composable para paginação, filtros e ordenação
const {
  items,
  totalItems,
  page,
  limit,
  filters,
  sort,
  arrow,
  load,
} = useSortableTable('/financeiro/planos-contas', { key: 'nome', order: 'asc' });

// inicializa filtro de nome
Object.assign(filters.value, { nome: '' });


// opções de limite de página
const pageOptions = [10, 15, 20, 25, 50];

// computeds para resumo
const startRow = computed(() =>
  (page.value - 1) * limit.value + 1
);
const endRow = computed(() =>
  Math.min(page.value * limit.value, totalItems.value)
);

// watchers para recarregar ao mudar página ou limite
watch(page, () => load());
watch(limit, () => {
  page.value = 1;
  load();
});

// navegação e CRUD
function irParaCadastro() {
  router.push('/financeiro/planos-contas/novo');
}
async function excluir(id) {
  if (!confirm('Confirma exclusão deste plano de contas?')) return;
  try {
    const resp = await axios.delete(`/financeiro/planos-contas/${id}`);
    const deleted = resp?.data?.deleted === true || resp?.status === 204;
    const message = resp?.data?.message;

    load();

    if (deleted) {
      alert(message || 'Plano excluído com sucesso.');
    } else {
      alert(
        message ||
        'O plano não pôde ser excluído. Verifique se há subcontas ou lançamentos vinculados.'
      );
    }
  } catch (e) {
    const status = e.response?.status;
    const msg    = e.response?.data?.error;
    if (status === 409) {
      alert(msg || 'Não é possível excluir: existem vínculos com este plano.');
    } else if (status === 404) {
      alert('Plano de contas não encontrado.');
    } else {
      alert(msg || 'Erro ao excluir plano de contas.');
    }
  }
}

// filtros simples
function filtrar() {
  page.value = 1;
  load();
}
function limparFiltros() {
  page.value = 1;
  filters.value.nome = '';
  load();
}

onMounted(async () => {
  await fetchPermissions();
  load();
});
</script>

<style scoped>
.same-height { height: 30px; }

:deep(.pagination.pagination-sm .page-link) {
  padding: rem rem;
  line-height: 1;
  font-size: 0.8rem !important;
  border-radius: 0 !important;
}

</style>
